import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { PlusIcon } from 'lucide-react'
import { Button, Input } from '@repo/ui'
import { SUB_SOURCE_KIND_LABELS, subSourceKey, type SourceChannel, type SubSourceKind, type SubSourceRef } from '@repo/types'

import { SelectField, SELECT_FIELD_FOOTER_ROW_SX, type SelectItem } from 'components/input/fields/SelectField'
import { useSnackbar } from 'contexts'
import { useAddSalesSubSource, useSalesSubSources } from 'hooks/api/useGtmSalesPipeline'
import { getErrorMessage } from 'utils'

import { MANAGE_SALES_TIP } from './dealsModel'

const NONE = ''

/** `kind:id` ↔ the stored reference. */
const decode = (v: string): { kind: SubSourceKind; id: string } | null => {
  const i = v.indexOf(':')
  return i > 0 ? { kind: v.slice(0, i) as SubSourceKind, id: v.slice(i + 1) } : null
}

/**
 * VRG-701 — the sub-source picker (Spencer, 2026-10-14). Its options depend on the CHANNEL:
 * Trade shows → the trade-show calendar, Sales → outreach campaigns, Content / SEO → articles,
 * Affiliate programs → referrers, and every channel → its saved list, which "Add sub-source"
 * (gated "Manage sales", §27: shown disabled with the permission named) grows inline. The value
 * is a typed reference; the label shown is the snapshot the API stored.
 *
 * Built on the house `SelectField` (search, groups, footer row — §28), not a raw MUI menu.
 */
export const SubSourceField = ({
  channel,
  value,
  onChange,
  canManage,
  label = 'Sub-source (optional)',
}: {
  /** Null = no channel picked: the field is disabled, since a sub-source belongs to a channel. */
  channel: SourceChannel | null
  value: SubSourceRef | null
  onChange: (v: { kind: SubSourceKind; id: string } | null) => void
  canManage: boolean
  label?: string
}) => {
  const { open: notify } = useSnackbar()
  const { data, isLoading } = useSalesSubSources(channel)
  const add = useAddSalesSubSource()
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  // The menu keeps focus on its list; move it into the new field once it renders.
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!adding) return
    const t = setTimeout(() => inputRef.current?.focus(), 50)
    return () => clearTimeout(t)
  }, [adding])

  const options = useMemo<SelectItem[]>(() => {
    const out: SelectItem[] = [{ value: NONE, label: 'None' }]
    if (data) {
      for (const o of data.options) out.push({ value: subSourceKey(o), label: o.label, description: o.hint ?? undefined, group: SUB_SOURCE_KIND_LABELS[o.kind] })
      for (const o of data.saved) out.push({ value: subSourceKey(o), label: o.label, group: SUB_SOURCE_KIND_LABELS.saved })
    }
    // The stored pick still shows if its list no longer has it (a campaign since deleted).
    if (value && value.label && !out.some((o) => o.value === subSourceKey(value))) {
      out.push({ value: subSourceKey(value), label: value.label, group: 'Current' })
    }
    return out
  }, [data, value])

  const save = () => {
    const label = draft.trim()
    if (!label || !channel) return
    add.mutate(
      { channel, label },
      {
        onSuccess: (o) => {
          setDraft('')
          setAdding(false)
          onChange({ kind: o.kind, id: o.id })
          notify({ type: 'success', title: `Sub-source "${o.label}" saved for this channel` })
        },
        onError: (e) => notify({ type: 'error', title: getErrorMessage(e as Error) }),
      },
    )
  }

  const footer = !channel ? undefined : adding ? (
    // Typing here must not reach the menu's type-ahead.
    <Stack direction="row" spacing="8px" alignItems="center" sx={{ px: '8px', py: '6px' }} onKeyDown={(e) => e.stopPropagation()} data-testid="sub-source-add-form">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Input
          placeholder="Name the sub-source"
          value={draft}
          onChange={setDraft}
          handleKeyDown={(e) => {
            if (e.key === 'Enter') save()
            if (e.key === 'Escape') setAdding(false)
          }}
          inputRef={inputRef}
          inputProps={{ 'aria-label': 'New sub-source name' }}
        />
      </Box>
      <Button variant="primary" disableElevation onClick={save} loading={add.isPending} disabled={!draft.trim()}>
        Add
      </Button>
    </Stack>
  ) : (
    <Box
      role="button"
      tabIndex={0}
      aria-disabled={!canManage}
      title={canManage ? undefined : MANAGE_SALES_TIP}
      onClick={() => canManage && setAdding(true)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && canManage) setAdding(true)
      }}
      sx={{ ...SELECT_FIELD_FOOTER_ROW_SX, opacity: canManage ? 1 : 0.5, cursor: canManage ? 'pointer' : 'not-allowed' }}
      data-testid="sub-source-add"
    >
      <PlusIcon size={16} strokeWidth={1} />
      Add sub-source
    </Box>
  )

  return (
    <Stack spacing={1} sx={{ minWidth: 0, width: '100%' }}>
      <SelectField
        label={label}
        placeholder={channel ? (isLoading ? 'Loading…' : 'None') : 'Pick a channel first'}
        disabled={!channel}
        search={options.length > 6}
        options={options}
        value={value ? subSourceKey(value) : NONE}
        onChange={(v) => onChange(String(v) === NONE ? null : decode(String(v)))}
        footer={footer}
      />
      {channel && data && data.kind === null && data.saved.length === 0 && (
        <Typography variant="body4" color="text.secondary">
          No sub-sources saved for this channel yet.
        </Typography>
      )}
    </Stack>
  )
}
